import { formatCurrency, pluralize } from "@sellar-io/common";
import { BadgeGreen } from "../../../components/badge/Badge";
import { Card, Icon } from "@sellar-io/components";
import { ReactNode } from "react";
import { Alert } from "~/components/alert/Alert";

type PaidOrderCardProps = {
  supplierName: string;
  itemCount: number;
  amount: number;
  currency: "gbp" | "sgd";
  paymentStatus?: "succeeded" | "pending" | "failed";
  paymentRetry?: ReactNode;
  paymentFailureReason?: string;
};

export const PaidOrderCard = ({
  supplierName,
  itemCount,
  amount,
  currency,
  paymentStatus = "succeeded",
  paymentRetry,
  paymentFailureReason = "Your payment was declined",
}: PaidOrderCardProps) => {
  return (
    <Card
      className={`overflow-hidden rounded-2xl border shadow-sm transition-all ${paymentStatus === "failed" ? "bg-white" : paymentStatus === "pending" ? "border-amber-200 bg-amber-50/50" : "border-emerald-100 bg-emerald-50/30"}`}
    >
      <div className="flex flex-col gap-3 rounded-2xl p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 sm:flex-1 sm:justify-start sm:gap-4">
          <Icon
            icon={
              paymentStatus === "failed"
                ? "ALERT_FILLED"
                : paymentStatus === "pending"
                  ? "CLOCK_OUTLINE"
                  : "CHECKBOX_MARKED_CIRCLE"
            }
            size={0.8}
            className={
              paymentStatus === "failed"
                ? "text-red-800"
                : paymentStatus === "pending"
                  ? "text-amber-700"
                  : "text-emerald-700"
            }
          />
          <div className="flex flex-col">
            <span className="font-semibold">{supplierName}</span>
            <span className="text-zinc-600">
              {itemCount} {pluralize("item", itemCount)}
            </span>
          </div>
        </div>
        <div className="order-first flex items-center justify-between gap-3 sm:order-none sm:justify-end sm:gap-4">
          {paymentStatus === "pending" ? (
            <div className="flex flex-col items-end gap-1 text-right">
              <span className="flex items-center gap-1 text-sm font-semibold text-amber-700">
                <Icon icon="CLOCK_OUTLINE" size={0.65} />
                Payment pending
              </span>
            </div>
          ) : paymentStatus === "succeeded" ? (
            <BadgeGreen
              className="self-center font-semibold"
              icon="CHECKBOX_MARKED_CIRCLE"
            >
              Order placed
            </BadgeGreen>
          ) : null}
          <div className="flex shrink-0 flex-col items-end">
            {paymentStatus === "failed" && (
              <span className="text-sm font-medium text-red-800">
                Payment failed
              </span>
            )}
            <span className="font-semibold">
              {formatCurrency(amount, currency)}
            </span>
            <span className="text-zinc-600">inc. VAT</span>
          </div>
        </div>
      </div>
      {paymentStatus === "failed" && (
        <div className="p-4 pt-0">
          <Alert type="error" message="" className="w-full">
            <div className="text-base text-zinc-800">
              <p className="font-semibold">Payment failed</p>
              <p>
                Your order with {supplierName} has been created, but your
                payment has failed. Reason: {paymentFailureReason}
              </p>
            </div>

            {paymentRetry}
          </Alert>
        </div>
      )}
    </Card>
  );
};
